import React, { useState, useEffect, useRef } from "react";
import secureLocalStorage from "react-secure-storage";
import config from "../config.json";
import { getData, processVoiceWithGemini } from "../service/APIService";
import { useSubscription } from "../context/SubscriptionContext";
import { useNavigate } from "react-router-dom";
import { FaTimes, FaGlobeAmericas, FaChevronDown } from "react-icons/fa";
import { HiMiniMicrophone, HiStop } from "react-icons/hi2";

// Modern AI vector icons matching theme (Heroicons 2)
const ModernMicIcon = ({ size = 16, className = "" }) => (
  <HiMiniMicrophone
    size={size}
    className={className}
    style={{ display: "inline-block", verticalAlign: "middle" }}
  />
);

const ModernStopIcon = ({ size = 13, className = "" }) => (
  <HiStop
    size={size}
    className={className}
    style={{ display: "inline-block", verticalAlign: "middle" }}
  />
);

const CUISINE_OPTIONS = [
  { label: "All Cuisines (Global)", value: "", short: "All" },
  { label: "Italian", value: "Italian", short: "Italian" },
  { label: "Indian", value: "Indian", short: "Indian" },
  { label: "Chinese", value: "Chinese", short: "Chinese" },
  { label: "Japanese", value: "Japanese", short: "Japanese" },
  { label: "American", value: "American", short: "American" },
  { label: "Mexican", value: "Mexican", short: "Mexican" },
  { label: "French", value: "French", short: "French" },
  { label: "Spanish", value: "Spanish", short: "Spanish" },
  { label: "Thai", value: "Thai", short: "Thai" },
  { label: "Greek", value: "Greek", short: "Greek" },
  { label: "Mediterranean", value: "Mediterranean", short: "Mediterranean" },
  { label: "Korean", value: "Korean", short: "Korean" },
  { label: "Middle Eastern", value: "Middle Eastern", short: "Middle Eastern" },
];

const POPULAR_CUISINES = CUISINE_OPTIONS.slice(0, 7);

const RecipeInputForm = ({ onFetchRecipe, loading, activeTab, setActiveTab }) => {
  const { isSubscribed, reachedLimit, recipeCount: contextRecipeCount, incrementScanCount } = useSubscription();
  const navigate = useNavigate();
  // Pro users get the full daily limit; non-pro (free) users get the smaller limit.
  const dailyLimit = isSubscribed ? config["pro-dailyLimit"] : config["non-pro-dailyLimit"];
  const [recipeName, setRecipeName] = useState("");
  const [ingredients, setIngredients] = useState([]);
  const [cuisine, setCuisine] = useState("");
  const [preference, setPreference] = useState("No Preference");
  const [inputValue, setInputValue] = useState("");
  const [dailyCount, setDailyCount] = useState(0);
  const [createdRecipes, setCreatedRecipes] = useState(0);
  const [remainingRecipes, setRemainingRecipes] = useState(0);
  const [ingredientsCount] = useState(10);
  const [isGuest, setIsGuest] = useState(false);
  const [checkingLimit, setCheckingLimit] = useState(false);

  // Voice Recognition states (Powered by Gemini AI)
  const [isListening, setIsListening] = useState(false);
  const [voiceTranscript, setVoiceTranscript] = useState("");
  const [isProcessingVoice, setIsProcessingVoice] = useState(false);
  const [voiceFeedback, setVoiceFeedback] = useState("");
  const [recognitionInstance, setRecognitionInstance] = useState(null);

  // Custom Theme-Friendly Cuisine Dropdown State
  const [isCuisineOpen, setIsCuisineOpen] = useState(false);
  const cuisineRef = useRef(null);

  useEffect(() => {
    const handleOutsideClick = (e) => {
      if (cuisineRef.current && !cuisineRef.current.contains(e.target)) {
        setIsCuisineOpen(false);
      }
    };
    document.addEventListener("mousedown", handleOutsideClick);
    document.addEventListener("touchstart", handleOutsideClick);
    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
      document.removeEventListener("touchstart", handleOutsideClick);
    };
  }, []);

  const groceries = [
    "Avacado",
    "Prawn",
    "Gongura",
    "Spinach",
    "Palak Spinach",
    "Mint Leaves",
    "Chenna Dhal",
    "Urad Dhall",
    "Green Chillie",
    "Curd",
    "Tomato",
    "Onion",
    "Garlic",
    "Potato",
    "Carrot",
    "Cabbage",
    "Egg",
    "Chicken",
    "Beef",
    "Fish",
    "Rice",
    "Wheat Flour",
    "Milk",
    "Cheese",
    "Butter",
    "Salt",
    "Pepper",
    "Olive Oil",
    "Spinach",
    "Cucumber",
    "Corn",
    "Peas",
    "Sugar",
    "Lentils",
    "Beans",
    "Pasta",
    "Bread",
    "Chili Powder",
    "Ginger",
    "Yogurt",
    "Apple",
    "Banana",
    "Orange",
    "Strawberry",
    "Blueberry",
    "Pineapple",
    "Mango",
    "Papaya",
    "Avocado",
    "Zucchini",
    "Broccoli",
    "Cauliflower",
    "Celery",
    "Bell Pepper",
    "Sweet Potato",
    "Pumpkin",
    "Eggplant",
    "Mushroom",
    "Okra",
    "Beetroot",
    "Radish",
    "Leek",
    "Lettuce",
    "Kale",
    "Swiss Chard",
    "Bok Choy",
    "Parsley",
    "Cilantro",
    "Basil",
    "Mint",
    "Rosemary",
    "Thyme",
    "Oregano",
    "Sage",
    "Dill",
    "Tarragon",
    "Chives",
    "Fennel",
    "Lime",
    "Lemon",
    "Grapes",
    "Watermelon",
    "Cantaloupe",
    "Peach",
    "Plum",
    "Cherry",
    "Raspberry",
    "Blackberry",
    "Kiwi",
    "Guava",
    "Coconut",
    "Dragon Fruit",
    "Lychee",
    "Dates",
    "Figs",
    "Almonds",
    "Cashews",
    "Pistachios",
    "Walnuts",
    "Peanuts",
    "Hazelnuts",
    "Sunflower Seeds",
    "Pumpkin Seeds",
    "Chia Seeds",
    "Flax Seeds",
    "Sesame Seeds",
    "Coconut Milk",
    "Almond Milk",
    "Soy Milk",
    "Oat Milk",
    "Rice Milk",
    "Quinoa",
    "Barley",
    "Oats",
    "Couscous",
    "Polenta",
    "Semolina",
    "Tapioca",
    "Cornmeal",
    "Chickpea Flour",
    "Buckwheat",
    "Rye Flour",
    "Spelt Flour",
    "Whole Wheat Flour",
    "Bread Crumbs",
    "Panko",
    "Vanilla Extract",
    "Almond Extract",
    "Maple Syrup",
    "Honey",
    "Molasses",
    "Brown Sugar",
    "Powdered Sugar",
    "Cocoa Powder",
    "Baking Soda",
    "Baking Powder",
    "Cornstarch",
    "Gelatin",
    "Agar-Agar",
    "Yeast",
    "Butter Milk",
    "Cream Cheese",
    "Heavy Cream",
    "Sour Cream",
    "Cottage Cheese",
    "Paneer",
    "Tofu",
    "Tempeh",
    "Seitan",
    "Edamame",
    "Black Beans",
    "Kidney Beans",
    "White Beans",
    "Navy Beans",
    "Cannellini Beans",
    "Pinto Beans",
    "Lima Beans",
    "Fava Beans",
    "Soybeans",
    "Green Beans",
    "Chickpeas",
    "Hummus",
    "Tahini",
    "Peanut Butter",
    "Almond Butter",
    "Cashew Butter",
    "Jelly",
    "Jam",
    "Marmalade",
    "Pickles",
    "Relish",
    "Mayonnaise",
    "Ketchup",
    "Mustard",
    "Barbecue Sauce",
    "Hot Sauce",
    "Soy Sauce",
    "Fish Sauce",
    "Hoisin Sauce",
    "Oyster Sauce",
    "Worcestershire Sauce",
    "Vinegar",
    "Balsamic Vinegar",
    "Rice Vinegar",
    "Apple Cider Vinegar",
    "White Vinegar",
    "Miso",
    "Curry Paste",
    "Curry Powder",
    "Turmeric",
    "Coriander",
    "Cumin",
    "Cardamom",
    "Cloves",
    "Cinnamon",
    "Nutmeg",
    "Allspice",
    "Star Anise",
    "Black Pepper",
    "White Pepper",
    "Paprika",
    "Smoked Paprika",
    "Cayenne Pepper",
    "Red Chili Flakes",
    "Saffron",
    "Fenugreek",
    "Mustard Seeds",
    "Caraway",
    "Sumac",
    "Za'atar",
    "Garam Masala",
    "Herbes de Provence",
    "Italian Seasoning",
    "Bay Leaves",
    "Bouillon Cubes",
    "Chicken Stock",
    "Beef Stock",
    "Vegetable Stock",
    "Bone Broth",
    "Tomato Paste",
    "Tomato Sauce",
    "Crushed Tomatoes",
    "Diced Tomatoes",
    "Tomato Juice",
    "Kale Chips",
    "Seaweed Snacks",
    "Rice Crackers",
    "Popcorn",
    "Tortilla Chips",
    "Potato Chips",
    "Pretzels",
    "Granola Bars",
    "Trail Mix",
    "Dark Chocolate",
    "Milk Chocolate",
    "White Chocolate",
    "Ice Cream",
    "Sorbet",
    "Frozen Yogurt",
    "Frozen Berries",
    "Frozen Vegetables",
    "Frozen Pizza",
    "Frozen French Fries",
    "Frozen Meatballs",
    "Frozen Fish Fillets",
    "Shrimp",
    "Crab",
    "Lobster",
    "Clams",
    "Mussels",
    "Scallops",
    "Squid",
    "Octopus",
    "Salmon",
    "Tuna",
    "Cod",
    "Haddock",
    "Trout",
    "Mackerel",
    "Sardines",
    "Anchovies",
    "Duck",
    "Turkey",
    "Goose",
    "Venison",
    "Lamb",
    "Pork",
    "Ham",
    "Bacon",
    "Sausage",
    "Hot Dogs",
    "Meatballs",
    "Ground Beef",
    "Ground Turkey",
    "Ground Chicken",
    "Ground Pork",
    "Breakfast Cereal",
    "Oatmeal",
    "Granola",
    "Cornflakes",
    "Rice Krispies",
    "Muesli",
    "Pancake Mix",
    "Waffle Mix",
    "Biscuits",
    "Crackers",
    "Bagels",
    "English Muffins",
    "Croissants",
    "Pastries",
    "Muffins",
    "Cupcakes",
    "Brownies",
    "Cookies",
    "Cake Mix",
    "Pie Crust",
    "Pie Filling",
    "Canned Soup",
    "Instant Noodles",
    "Macaroni and Cheese",
    "Lasagna Sheets",
    "Ravioli",
    "Gnocchi",
    "Tortellini",
    "Spring Roll Wrappers",
    "Wonton Wrappers",
    "Rice Paper",
    "Nori Sheets",
    "Tofu Skin",
    "Hot Chocolate Mix",
    "Tea",
    "Green Tea",
    "Black Tea",
    "Herbal Tea",
    "Coffee",
    "Espresso",
    "Decaf Coffee",
    "Iced Coffee",
    "Coffee Creamer",
    "Soft Drinks",
    "Juice",
    "Smoothies",
    "Energy Drinks",
    "Sports Drinks",
    "Sparkling Water",
    "Mineral Water",
    "Beer",
    "Wine",
    "Whiskey",
    "Vodka",
    "Rum",
    "Tequila",
    "Gin",
    "Liqueur",
    "Cocktail Mixers",
    "Champagne",
    "Cider",
    "Sake",
    "Mead",
  ];
  useEffect(() => {
    const fetchRemainingLimit = async () => {
      setCheckingLimit(true);
      const loggedInUser = JSON.parse(secureLocalStorage.getItem("loggedInUser"));
      setIsGuest(!loggedInUser);
      const email = loggedInUser?.email || "guest";
      // Use local date to avoid UTC reset at 6-7pm local time
      const d = new Date();
      const today = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

      if (email === "guest") {
        const currentGuestCount = contextRecipeCount;
        setCreatedRecipes(currentGuestCount);
        setDailyCount(currentGuestCount); // For guests, we treat total as daily since it's a lifetime limit
        setRemainingRecipes(Math.max(0, config.guestCount - currentGuestCount));
        setCheckingLimit(false);
        return;
      }

      try {
        // Fetch recipes from DB for this user
        const dbItems = await getData(email, "recipe-ai");
        const totalCount = dbItems.length;
        setCreatedRecipes(totalCount);

        // Track quota consumed today so deleting a recipe from history does NOT restore daily quota
        const recipeData = JSON.parse(secureLocalStorage.getItem("recipeData")) || {};
        const recordedTodayUsage = recipeData[today]?.[email] || 0;

        // Filter those created today in DB
        const todayItems = dbItems.filter(item => item.date && item.date.startsWith(today));
        const effectiveTodayCount = Math.max(todayItems.length, recordedTodayUsage);

        // Keep local storage synchronized with at least the highest count seen
        if (effectiveTodayCount > recordedTodayUsage) {
          if (!recipeData[today]) recipeData[today] = {};
          recipeData[today][email] = effectiveTodayCount;
          secureLocalStorage.setItem("recipeData", JSON.stringify(recipeData));
        }

        setDailyCount(effectiveTodayCount);
        setRemainingRecipes(Math.max(0, dailyLimit - effectiveTodayCount));
      } catch (error) {
        console.error("Error fetching limit from DB:", error);
        // Fallback to minimal state
        setRemainingRecipes(0);
      } finally {
        setCheckingLimit(false);
      }
    };

    fetchRemainingLimit();
  }, [contextRecipeCount, dailyLimit]);
  const handleAddIngredient = (ingredient) => {
    if (ingredient && !ingredients.includes(ingredient)) {
      if (ingredients.length < ingredientsCount) {
        setIngredients((prev) => [...prev, ingredient]);
      } else {
        alert("You can add a maximum of 10 ingredients.");
      }
    }
    setInputValue("");
  };

  const handleRemoveIngredient = (ingredientToRemove) => {
    setIngredients((prev) => prev.filter((ingredient) => ingredient !== ingredientToRemove));
  };

  const handleVoiceProcess = async (text) => {
    if (!text || !text.trim()) return;
    setIsProcessingVoice(true);
    setVoiceFeedback("✨ Gemini AI is analyzing ingredients & recipe...");

    try {
      const parsed = await processVoiceWithGemini(text);
      console.log("Gemini Voice parsed:", parsed);

      const detectedIngredients = parsed.ingredients || [];
      const detectedRecipeName = parsed.recipeName || "";
      const detectedCuisine = parsed.cuisine || cuisine || "Chef's Choice (Global)";
      const detectedPreference = parsed.preference || preference || "No Preference";
      const detectedAction = parsed.action || (detectedIngredients.length > 0 ? "full" : "recipe_by_name");

      if (detectedAction === "recipe_by_name" && detectedRecipeName) {
        setActiveTab("name");
        setRecipeName(detectedRecipeName);
      } else if (detectedIngredients.length > 0) {
        setActiveTab("ingredients");
        setIngredients((prev) => Array.from(new Set([...prev, ...detectedIngredients])));
      } else if (detectedRecipeName) {
        setActiveTab("name");
        setRecipeName(detectedRecipeName);
      } else {
        // Fallback: if no structured items found, fill the current input box with spoken text
        if (activeTab === "name") {
          setRecipeName(text);
        } else {
          setInputValue(text);
        }
      }

      if (detectedCuisine) {
        setCuisine(detectedCuisine);
      }
      if (detectedPreference && detectedPreference !== "No Preference") {
        setPreference(detectedPreference);
      }

      setIsProcessingVoice(false);
      setVoiceTranscript("");
      setVoiceFeedback(`✅ Understood: "${text}". Review and tap "Create Recipe"!`);
      setTimeout(() => setVoiceFeedback(""), 5000);
    } catch (err) {
      console.error("Voice processing error:", err);
      setIsProcessingVoice(false);
      // Fallback: put raw voice text into active input so user still sees what they spoke
      if (activeTab === "name") {
        setRecipeName(text);
      } else {
        setInputValue(text);
      }
      setVoiceFeedback("Couldn't analyze with AI, but added your speech to the text box.");
      setTimeout(() => setVoiceFeedback(""), 4000);
    }
  };

  const startVoiceRecognition = () => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) {
      alert("Voice recognition is supported in Chrome, Safari, and Edge. You can also use dictation on your device keyboard!");
      return;
    }

    try {
      const recognition = new SpeechRecognition();
      recognition.continuous = false;
      recognition.interimResults = true;
      recognition.lang = "en-US";

      let finalSpeech = "";

      recognition.onstart = () => {
        setIsListening(true);
        setVoiceTranscript("");
        setVoiceFeedback("Listening... Say your ingredients & recipe (e.g. 'Eggs, onion, and cheese')");
      };

      recognition.onresult = (event) => {
        let interim = "";
        for (let i = event.resultIndex; i < event.results.length; ++i) {
          if (event.results[i].isFinal) {
            finalSpeech += event.results[i][0].transcript;
          } else {
            interim += event.results[i][0].transcript;
          }
        }
        setVoiceTranscript(finalSpeech || interim);
      };

      recognition.onerror = (event) => {
        console.warn("Speech recognition error:", event.error);
        setIsListening(false);
        if (event.error === "not-allowed") {
          setVoiceFeedback("Microphone permission denied. Please allow microphone access.");
        } else if (event.error !== "no-speech") {
          setVoiceFeedback("Voice recognition error. Please try again.");
        }
        setTimeout(() => setVoiceFeedback(""), 3500);
      };

      recognition.onend = () => {
        setIsListening(false);
        const textToProcess = finalSpeech.trim() || voiceTranscript.trim();
        if (textToProcess) {
          handleVoiceProcess(textToProcess);
        } else {
          setVoiceFeedback("");
        }
      };

      setRecognitionInstance(recognition);
      recognition.start();
    } catch (err) {
      console.error("Failed to start speech recognition:", err);
      setIsListening(false);
    }
  };

  const stopVoiceRecognition = () => {
    if (recognitionInstance) {
      recognitionInstance.stop();
    }
    setIsListening(false);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const email = JSON.parse(secureLocalStorage.getItem("loggedInUser"))?.email || "guest";
    const d = new Date();
    const today = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    const recipeData = JSON.parse(secureLocalStorage.getItem("recipeData")) || {};
    const effectiveCuisine = cuisine || "Chef's Choice (Global)";

    if (activeTab === "ingredients") {
      if (ingredients.length > 0) {
        onFetchRecipe(ingredients.join(", "), effectiveCuisine, preference, "full");

        if (email === "guest") {
          incrementScanCount();
        } else {
          if (!recipeData[today]) recipeData[today] = {};
          recipeData[today][email] = (recipeData[today][email] || 0) + 1;
          secureLocalStorage.setItem("recipeData", JSON.stringify(recipeData));
        }

        setIngredients([]);
        setCreatedRecipes((prev) => prev + 1);
        setDailyCount((prev) => prev + 1);
        setRemainingRecipes((prev) => prev - 1);
      } else {
        alert("Please enter ingredients you have in your fridge or speak them using the mic button.");
      }
    } else {
      if (recipeName.trim()) {
        onFetchRecipe("", effectiveCuisine, preference, "recipe_by_name", recipeName.trim());

        if (email === "guest") {
          incrementScanCount();
        } else {
          if (!recipeData[today]) recipeData[today] = {};
          recipeData[today][email] = (recipeData[today][email] || 0) + 1;
          secureLocalStorage.setItem("recipeData", JSON.stringify(recipeData));
        }

        setRecipeName("");
        setCreatedRecipes((prev) => prev + 1);
        setDailyCount((prev) => prev + 1);
        setRemainingRecipes((prev) => prev - 1);
      } else {
        alert("Please enter a dish name or speak it using the mic button.");
      }
    }
  };

  const handleInputChange = (e) => {
    setInputValue(e.target.value);
  };

  const filteredGroceries = groceries.filter((grocery) => grocery.toLowerCase().includes(inputValue.toLowerCase()));

  return (
    <form onSubmit={handleSubmit}>
      {/* AI Food Assistant Banner matching Reference Mockup (media_1793017968504.png) */}
      <div 
        className="ai-food-assistant-card mb-3 p-3 rounded-4"
        style={{
          background: isListening ? "linear-gradient(135deg, #fef2f2 0%, #fff1f2 100%)" : "#ffffff",
          border: isListening ? "1.5px solid #ef4444" : "1px solid rgba(0, 0, 0, 0.08)",
          boxShadow: isListening ? "0 4px 18px rgba(239, 68, 68, 0.15)" : "0 2px 8px rgba(0, 0, 0, 0.03)"
        }}
      >
        <div className="d-flex align-items-center justify-content-between gap-2">
          <div className="min-w-0 flex-grow-1">
            <div className="d-flex align-items-center gap-1.5 mb-0.5">
              <span className="fw-bold text-dark" style={{ fontSize: "0.95rem", letterSpacing: "-0.2px" }}>
                AI food assistant
              </span>
              <span className="badge rounded-pill bg-success bg-opacity-10 text-success border border-success border-opacity-25" style={{ fontSize: "9px", padding: "2px 6px" }}>
                Gemini
              </span>
              {isListening && <span className="badge rounded-pill bg-danger text-white px-2" style={{ fontSize: "9px" }}>LIVE</span>}
            </div>
            <p className="text-secondary mb-0 small text-truncate" style={{ fontSize: "12px" }}>
              {isListening
                ? (voiceTranscript || "Listening... Speak ingredients you have")
                : (voiceFeedback || "Smart recipe creator powered by Gemini")}
            </p>
          </div>

          <button
            type="button"
            className={`ai-speak-btn flex-shrink-0 ${isListening ? "listening" : ""} ${isProcessingVoice ? "processing" : ""}`}
            onClick={isListening ? stopVoiceRecognition : startVoiceRecognition}
            disabled={isProcessingVoice}
            title={isListening ? "Tap to stop listening" : isProcessingVoice ? "Analyzing speech with Gemini..." : "Tap to speak ingredients with Gemini AI"}
          >
            {isProcessingVoice ? (
              <>
                <span className="spinner-border spinner-border-sm me-1" role="status" style={{ width: "12px", height: "12px", borderWidth: "1.5px" }}></span>
                <span>Thinking...</span>
              </>
            ) : isListening ? (
              <>
                <span className="soundwave-container me-1">
                  <span className="soundwave-bar"></span>
                  <span className="soundwave-bar"></span>
                  <span className="soundwave-bar"></span>
                  <span className="soundwave-bar"></span>
                </span>
                <span>Listening...</span>
              </>
            ) : (
              <>
                <span className="speak-mic-circle">
                  <ModernMicIcon size={14} />
                </span>
                <span>Speak</span>
              </>
            )}
          </button>
        </div>
      </div>

      {activeTab === "ingredients" ? (
        <div className="mt-0">
          <div className="d-flex justify-content-between align-items-center mb-1.5">
            <label className="mb-0">
              <small className="fw-bold text-dark">What's in your Fridge / Pantry? (🧅🍅🧄🌶️🥦)</small>
            </label>
            <span className="badge rounded-pill bg-light text-secondary border" style={{ fontSize: "11px" }}>
              {ingredients.length}/{ingredientsCount}
            </span>
          </div>

          {ingredients.length > 0 && (
            <div className="d-flex flex-wrap gap-2 mb-2 p-2 rounded-4" style={{ background: "rgba(240, 253, 244, 0.6)", border: "1px dashed rgba(34, 197, 94, 0.3)" }}>
              {ingredients.map((ingredient, index) => (
                <span key={index} className="ingredient-chip shadow-sm">
                  <span>{ingredient}</span>
                  <button
                    type="button"
                    className="ingredient-chip-remove"
                    onClick={() => handleRemoveIngredient(ingredient)}
                    title="Remove ingredient"
                  >
                    <i className="fi fi-rr-cross-small"></i>
                  </button>
                </span>
              ))}
            </div>
          )}

          <div style={{ position: "relative" }}>
            <div className="recipe-search-box">
              <div className="search-icon">
                <i className="fi fi-rr-search"></i>
              </div>
              <input
                type="text"
                value={inputValue}
                onChange={handleInputChange}
                placeholder="Add ingredients you have (e.g. Avocado, Eggs, Tomato)..."
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    if (inputValue.trim() && filteredGroceries.length > 0) {
                      handleAddIngredient(filteredGroceries[0]);
                    } else if (inputValue.trim()) {
                      handleAddIngredient(inputValue.trim());
                    }
                  }
                }}
              />
              <button
                type="button"
                className={`btn-inline-mic ${isListening ? "active" : ""}`}
                onClick={isListening ? stopVoiceRecognition : startVoiceRecognition}
                title={isListening ? "Tap to stop listening" : "Speak ingredients"}
                aria-label="Speak ingredients"
              >
                {isListening ? <ModernStopIcon size={12} /> : <ModernMicIcon size={15} />}
              </button>
              {inputValue.trim() && (
                <button
                  type="button"
                  className="btn-inline-add shadow-sm"
                  onClick={() => {
                    if (filteredGroceries.length > 0) {
                      handleAddIngredient(filteredGroceries[0]);
                    } else {
                      handleAddIngredient(inputValue.trim());
                    }
                  }}
                >
                  <i className="fi fi-rr-plus me-1" style={{ fontSize: "11px" }}></i> Add
                </button>
              )}
            </div>

            {inputValue && filteredGroceries.length > 0 && (
              <ul
                className="shadow-lg border-0 rounded-4 mt-2"
                style={{
                  listStyle: "none",
                  padding: "6px 0",
                  margin: "0",
                  backgroundColor: "#ffffff",
                  position: "absolute",
                  width: "100%",
                  maxHeight: "220px",
                  overflowY: "auto",
                  zIndex: 1000,
                  boxShadow: "0 12px 32px rgba(0, 0, 0, 0.12)",
                  border: "1px solid rgba(0, 0, 0, 0.08)"
                }}
              >
                {filteredGroceries.map((item, index) => {
                  const matchStart = item.toLowerCase().indexOf(inputValue.toLowerCase());
                  const matchEnd = matchStart + inputValue.length;
                  const before = item.slice(0, matchStart);
                  const match = item.slice(matchStart, matchEnd);
                  const after = item.slice(matchEnd);

                  return (
                    <li
                      key={index}
                      className="px-3 py-2 border-bottom border-light cursor-pointer d-flex align-items-center justify-content-between"
                      style={{ cursor: "pointer", transition: "background 0.15s ease" }}
                      onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "rgba(34, 197, 94, 0.08)")}
                      onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "transparent")}
                      onClick={() => handleAddIngredient(item)}
                    >
                      <span className="small text-dark">
                        {matchStart !== -1 ? (
                          <>
                            {before}
                            <span className="fw-bold text-success">{match}</span>
                            {after}
                          </>
                        ) : (
                          item
                        )}
                      </span>
                      <i className="fi fi-rr-plus-small text-muted" style={{ fontSize: "14px" }}></i>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </div>
      ) : (
        <div className="mt-0">
          <label className="mb-1.5"><small className="fw-bold text-dark">Craving a specific dish?</small></label>
          <div className="recipe-search-box">
            <div className="search-icon">
              <i className="fi fi-rr-restaurant"></i>
            </div>
            <input
              type="text"
              value={recipeName}
              onChange={(e) => setRecipeName(e.target.value)}
              placeholder="Enter dish name (e.g. Pasta Primavera, Veggie Burger)"
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  handleSubmit(e);
                }
              }}
            />
            <button
              type="button"
              className={`btn-inline-mic ${isListening ? "active" : ""}`}
              onClick={isListening ? stopVoiceRecognition : startVoiceRecognition}
              title={isListening ? "Tap to stop listening" : isProcessingVoice ? "Analyzing speech..." : "Speak dish name"}
              aria-label="Speak dish name"
              disabled={isProcessingVoice}
            >
              {isListening ? <ModernStopIcon size={12} /> : <ModernMicIcon size={15} />}
            </button>
          </div>
        </div>
      )}

      {/* Select Cuisine (Theme-friendly Neo-Minimalist Dropdown & Quick Chips) */}
      <div className="mt-3">
        <div className="d-flex justify-content-between align-items-center mb-1.5">
          <label className="mb-0">
            <small className="fw-bold text-dark" style={{ fontSize: "0.82rem", letterSpacing: "-0.2px" }}>
              Select Cuisine:
            </small>
          </label>
          {cuisine && (
            <button
              type="button"
              className="btn btn-link p-0 text-decoration-none text-muted"
              style={{ fontSize: "11px", fontWeight: "600" }}
              onClick={() => setCuisine("")}
            >
              Reset to Global
            </button>
          )}
        </div>

        <div className="cuisine-dropdown-wrapper" ref={cuisineRef}>
          {/* Custom Trigger Button matching Theme */}
          <button
            type="button"
            className={`cuisine-select-trigger ${isCuisineOpen ? "open" : ""} ${cuisine ? "has-selection" : ""}`}
            onClick={() => setIsCuisineOpen((prev) => !prev)}
            aria-haspopup="listbox"
            aria-expanded={isCuisineOpen}
          >
            <div className="cuisine-trigger-left">
              <div className="cuisine-icon-badge">
                <FaGlobeAmericas size={14} />
              </div>
              <span className="cuisine-selected-text">
                {CUISINE_OPTIONS.find((c) => c.value === cuisine)?.label || "All Cuisines (Global)"}
              </span>
            </div>

            <div className="cuisine-trigger-right">
              {cuisine && (
                <span
                  role="button"
                  className="cuisine-clear-btn"
                  onClick={(e) => {
                    e.stopPropagation();
                    setCuisine("");
                  }}
                  title="Clear cuisine"
                  aria-label="Clear cuisine"
                >
                  <FaTimes size={10} />
                </span>
              )}
              <div className={`cuisine-chevron-icon ${isCuisineOpen ? "rotated" : ""}`}>
                <FaChevronDown size={11} />
              </div>
            </div>
          </button>

          {/* Custom Themed Dropdown Popover */}
          {isCuisineOpen && (
            <div className="cuisine-custom-menu" role="listbox">
              <div className="cuisine-menu-scroll">
                {CUISINE_OPTIONS.map((item) => {
                  const isSelected = cuisine === item.value;
                  return (
                    <button
                      key={item.value || "all"}
                      type="button"
                      className={`cuisine-menu-item ${isSelected ? "selected" : ""}`}
                      onClick={() => {
                        setCuisine(item.value);
                        setIsCuisineOpen(false);
                      }}
                      role="option"
                      aria-selected={isSelected}
                    >
                      <span className="cuisine-item-label">{item.label}</span>
                      {isSelected && (
                        <span className="cuisine-item-check">
                          <i className="fi fi-rr-check"></i>
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {/* Quick-Pick Popular Cuisine Chips matching mockup */}
        <div className="cuisine-quick-chips-row">
          {POPULAR_CUISINES.map((chip) => {
            const isCurrent = cuisine === chip.value;
            return (
              <button
                key={chip.value || "all"}
                type="button"
                className={`cuisine-quick-chip ${isCurrent ? "active" : ""}`}
                onClick={() => setCuisine(chip.value)}
              >
                {chip.short}
              </button>
            );
          })}
        </div>
      </div>

      {/* Dietary Preference matching Reference Mockup Pill Chips */}
      <div className="mt-3 mb-2">
        <label className="d-block mb-1.5"><small className="fw-bold text-dark">Dietary Preference:</small></label>
        <div className="d-flex align-items-center gap-2 flex-wrap">
          {[
            {
              label: "Any",
              value: "No Preference",
              activeBg: "linear-gradient(135deg, #0f1f14 0%, #1e3a24 100%)",
              activeColor: "#ffffff",
              activeBorder: "1.5px solid #0f1f14",
              activeShadow: "0 3px 10px rgba(15, 31, 20, 0.3)",
              textShadow: "none"
            },
            {
              label: "Veg",
              value: "Veg",
              activeBg: "linear-gradient(135deg, #22c55e 0%, #16a34a 100%)",
              activeColor: "#ffffff",
              activeBorder: "1.5px solid #16a34a",
              activeShadow: "0 3px 12px rgba(34, 197, 94, 0.35)",
              textShadow: "0 1px 2px rgba(21, 128, 61, 0.3)"
            },
            {
              label: "Non-Veg",
              value: "Non-Veg",
              activeBg: "linear-gradient(135deg, #fca5a5 0%, #f87171 100%)",
              activeColor: "#ffffff",
              activeBorder: "1.5px solid #f87171",
              activeShadow: "0 3px 12px rgba(248, 113, 113, 0.35)",
              textShadow: "0 1px 2px rgba(185, 28, 28, 0.3)"
            }
          ].map((tab) => {
            const isSelected = preference === tab.value;
            return (
              <button
                key={tab.value}
                type="button"
                className="diet-pill-chip"
                style={{
                  background: isSelected ? tab.activeBg : "#ffffff",
                  color: isSelected ? tab.activeColor : "#374151",
                  border: isSelected ? tab.activeBorder : "1.5px solid #e5e7eb",
                  borderRadius: "9999px",
                  padding: "7px 16px",
                  fontSize: "0.82rem",
                  fontWeight: isSelected ? "600" : "500",
                  cursor: "pointer",
                  transition: "all 0.25s ease",
                  boxShadow: isSelected ? tab.activeShadow : "none",
                  textShadow: isSelected ? tab.textShadow : "none"
                }}
                onClick={() => setPreference(tab.value)}
              >
                {tab.label}
              </button>
            );
          })}
        </div>
      </div>

      <div className="mt-4">
        {(!isSubscribed && (reachedLimit || remainingRecipes <= 0 || dailyCount >= dailyLimit)) ? (
          <button
            type="button"
            className="w-100 py-3 rounded-pill text-white fw-bold shadow-sm"
            style={{
              background: "#0f1f14",
              border: "1px solid rgba(34, 197, 94, 0.35)",
              boxShadow: "0 4px 14px rgba(15, 31, 20, 0.35)",
              transition: "all 0.3s ease",
              color: "#ffffff"
            }}
            onClick={() => navigate("/upgrade")}
          >
            <i className="fi fi-rr-star me-2"></i>Upgrade to Pro
          </button>
        ) : (
          <button
            type="submit"
            className="w-100 py-3 rounded-pill text-white fw-bold shadow-sm"
            style={{
              background: (
                (isSubscribed && dailyCount >= dailyLimit) ||
                loading ||
                checkingLimit
              ) ? "#ccc" : "linear-gradient(135deg, #22c55e 0%, #16a34a 100%)",
              border: "none",
              boxShadow: "0 4px 14px rgba(34, 197, 94, 0.3)",
              transition: "all 0.3s ease"
            }}
            disabled={
              (isSubscribed && dailyCount >= dailyLimit) ||
              loading ||
              checkingLimit
            }
          >
            {checkingLimit ? "Checking Limit..."
              : loading ? "Generating Recipe..."
                : (isSubscribed && dailyCount >= dailyLimit) ? "Daily Limit Reached"
                  : activeTab === "ingredients" ? "Cook with My Fridge Ingredients" : "Create Dish Recipe"}
          </button>
        )}
        {/* ── Theme-Friendly Recipe Quota Card ── */}
        <div className="app-quota-card">
          <div className="quota-card-header">
            <div className="d-flex align-items-center gap-3">
              <div className={`quota-icon-wrap ${(isGuest ? remainingRecipes <= 0 : dailyCount >= dailyLimit) ? "exhausted" : ""}`}>
                <i className={`fas ${(isGuest ? remainingRecipes <= 0 : dailyCount >= dailyLimit) ? "fa-exclamation-circle" : "fa-calendar-check"}`}></i>
              </div>
              <div>
                <h6 className="quota-title">
                  {isGuest ? "Free Recipe Quota" : "Daily Recipe Quota"}
                </h6>
                <div className="quota-subtitle">
                  {isSubscribed ? (
                    <span className="text-success fw-semibold">
                      <i className="fas fa-sparkles me-1"></i> Pro Subscription Active
                    </span>
                  ) : (
                    <span>Free Tier Access</span>
                  )}
                </div>
              </div>
            </div>
            <span
              className={`quota-pill-badge ${(isGuest ? remainingRecipes <= 0 : dailyCount >= dailyLimit) ? "exhausted" : ""}`}
            >
              {isGuest
                ? `${createdRecipes} / ${config.guestCount}`
                : `${dailyCount} / ${dailyLimit}`}
            </span>
          </div>

          <div className="quota-progress-track">
            <div
              className={`quota-progress-bar ${(isGuest ? remainingRecipes <= 0 : dailyCount >= dailyLimit) ? "exhausted" : ""}`}
              style={{
                width: `${Math.min(100, (isGuest
                  ? (createdRecipes / config.guestCount)
                  : (dailyCount / dailyLimit)) * 100)}%`
              }}
            ></div>
          </div>

          <div className="quota-stats-row">
            <span className="quota-stat-item">
              <i className="fi fi-rr-check-circle text-success"></i>
              <span>{isGuest ? "Total Created" : "Today's Recipes"}:</span>
              <strong>{isGuest ? createdRecipes : dailyCount}</strong>
            </span>
            <span className="quota-stat-item">
              <span>Remaining:</span>
              <span className={`remaining-highlight ${(isGuest ? remainingRecipes <= 0 : dailyCount >= dailyLimit) ? "zero" : ""}`}>
                {isGuest ? remainingRecipes : Math.max(0, dailyLimit - dailyCount)} {isGuest ? (remainingRecipes === 1 ? "recipe" : "recipes") : (dailyLimit - dailyCount === 1 ? "recipe" : "recipes")}
              </span>
            </span>
          </div>

          {!isGuest && dailyCount >= dailyLimit && (
            <div className="quota-exhausted-alert">
              <i className="fi fi-rr-clock"></i>
              <span>Daily limit reached — resets at 12:00 AM midnight</span>
            </div>
          )}
        </div>
      </div>
    </form>
  );
};

export default RecipeInputForm;
